import { useState, type FormEvent, type ReactElement } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import { PasswordField } from "../components/PasswordField";
import { ResendVerification } from "../components/ResendVerification";

// Public (no-substudy) email signup. Substudy participants sign up with a
// personal study ID at /register/study-id instead — the shared study code
// was removed 2026-08-14.
export function Register(): ReactElement {
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register(email, password);
      // Login is gated on email verification, so we can't sign them in yet —
      // confirm and point them at their inbox.
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Registration failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Confirm your email" subtitle="One quick step to activate your account.">
        <p className="muted">
          We&apos;ve sent a verification link to <strong>{email}</strong>. Open it to activate your
          account, then sign in.
        </p>
        <ResendVerification email={email} />
        <p className="auth-alt">
          <Link to="/login">Continue to sign in</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Join the study" subtitle="How does your biological age compare to your chronological age?">
      <form onSubmit={onSubmit} className="auth-form">
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <PasswordField label="Password" value={password} onChange={setPassword} />
        <small className="field-hint">
          Avoid common or breached passwords; passphrases work well.
        </small>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" className="btn-primary-glow btn-block" disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>
      <p className="auth-alt">
        Given a study ID instead of using email?{" "}
        <Link to="/register/study-id">Sign up with your study ID</Link>
      </p>
      <p className="auth-alt">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
